import { type EntityType } from '@core/auth';

/** --- API (snake_case) --- */

export interface InvoiceApi {
  readonly id: number;
  readonly patient: {
    readonly id: number;
    readonly first_name: string;
    readonly last_name: string;
  };
  readonly due_date: string;
  readonly created: string;
  readonly due_amount: number;
  readonly status: string;
  readonly office_id: string;
  readonly office_address: string;
  readonly office_name: string;
  readonly payment_status: string;
  readonly attachments: readonly { readonly path: string; readonly url: string }[];
  readonly is_financially_responsible: boolean;
}

export interface BlockerApi {
  readonly id: number;
  readonly patient: {
    readonly id: number;
    readonly first_name: string;
    readonly last_name: string;
    readonly dob: string;
  };
  readonly step: { readonly order: number; readonly name: string };
  readonly description: string;
  readonly created: string;
  readonly activity: { readonly id: string; readonly type: string };
  readonly resolve_type: string;
  readonly form: { readonly schema: string };
}

export interface FacilityCycleApi {
  readonly id: number;
  readonly cycle_begin: string;
  readonly cycle_end: string;
  readonly cycle_delivery: string;
  readonly census_count: number;
  readonly changes_count: number;
  readonly invoices_count: number;
  readonly risks_count: number;
  readonly checked_in: boolean;
  readonly facility_name: string;
  readonly census: readonly unknown[];
  readonly changes: readonly unknown[];
  readonly invoices: readonly unknown[];
  readonly risks: readonly unknown[];
}

/** --- Domain (camelCase) --- */

export interface Invoice {
  readonly id: number;
  readonly patientId: number;
  readonly patientName: string;
  readonly dueDate: string;
  readonly created: string;
  readonly dueAmount: number;
  readonly status: string;
  readonly officeId: string;
  readonly officeName: string;
  readonly officeAddress: string;
  readonly paymentStatus: string;
  readonly isFinanciallyResponsible: boolean;
}

export interface Blocker {
  readonly id: number;
  readonly patientName: string;
  readonly patientId: number;
  readonly stepName: string;
  readonly stepOrder: number;
  readonly description: string;
  readonly created: string;
  readonly activityId: string;
  readonly activityType: string;
  readonly resolveType: string;
  readonly formSchema: string;
}

export type BlockerTone = 'urgent' | 'neutral';

export type BlockerActionKind = 'resolve' | 'addDocument' | 'accept' | 'reject';

export interface BlockerAction {
  readonly kind: BlockerActionKind;
  readonly label: string;
}

export interface FacilityCycle {
  readonly id: number;
  readonly cycleBegin: string;
  readonly cycleEnd: string;
  readonly cycleDelivery: string;
  readonly censusCount: number;
  readonly changesCount: number;
  readonly invoicesCount: number;
  readonly risksCount: number;
  readonly checkedIn: boolean;
  readonly facilityName: string;
}

export interface HomeDashboardData {
  readonly invoices: readonly Invoice[];
  readonly blockers: readonly Blocker[];
  readonly facilityCycles: readonly FacilityCycle[];
  readonly documentsReviewCount: number;
}

export type HomeRoleParam = EntityType;

/** Local device timezone greeting bucket (see `resolveLocalDayGreetingPeriod`). */
export type DayGreetingPeriod = 'morning' | 'afternoon' | 'evening';

/** TEMP: force empty home payloads to test empty-state UI. Set to `false` when done. */
export const FORCE_EMPTY_HOME_DATA = false;
